import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentosListPage = () => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const { isDarkMode } = useDarkMode();

  // Get selected category from URL, default to 'all'
  const selectedCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch documents and categories in parallel
        const [docsResponse, catsResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/documentos`),
          axios.get(`${BACKEND_URL}/api/documentos/categories`)
        ]);
        
        // Sort alphabetically by title for Phase 1
        const sortedDocs = docsResponse.data.documents.sort((a, b) => 
          a.title.localeCompare(b.title)
        );
        
        setAllDocuments(sortedDocs);
        
        // Filter out categories with 0 documents (empty categories)
        const nonEmptyCategories = catsResponse.data.categories.filter(cat => cat.count > 0);
        setCategories(nonEmptyCategories);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error al cargar documentos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter documents based on selected category
  const filteredDocuments = selectedCategory === 'all' 
    ? allDocuments 
    : allDocuments.filter(doc => doc.category === selectedCategory);

  const handleCategoryChange = (category) => {
    if (category === 'all') {
      setSearchParams({}); // Clear query params for 'all'
    } else {
      setSearchParams({ category });
    }
  };

  const handleDocumentClick = (slug) => {
    navigate(`/documentos/${slug}`);
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const handleAdminClick = () => {
    navigate('/admin/documentos');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        isDarkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-6 transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackClick}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-purple-700 hover:bg-purple-600' 
                    : 'bg-purple-700 hover:bg-purple-800'
                }`}
                aria-label="Volver"
              >
                <ArrowLeft size={24} />
              </button>
              <h1 className="text-3xl md:text-4xl font-bold">
                Documentos
              </h1>
            </div>
            
            {userType === 'admin' && (
              <button
                onClick={handleAdminClick}
                className={`p-3 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-purple-700 hover:bg-purple-600' 
                    : 'bg-purple-700 hover:bg-purple-800'
                }`}
                aria-label="Administrar documentos"
              >
                <Settings size={24} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Category Filter Tabs (Desktop) and Dropdown (Mobile) */}
      {!loading && !error && categories.length > 0 && (
        <div className={`border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="container mx-auto px-4">
            {/* Desktop: Horizontal Tabs */}
            <div className="hidden md:flex space-x-1 overflow-x-auto py-2">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? isDarkMode
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-600 text-white'
                    : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryChange(cat.name)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 whitespace-nowrap ${
                    selectedCategory === cat.name
                      ? isDarkMode
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-600 text-white'
                      : isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Mobile: Dropdown */}
            <div className="md:hidden py-3">
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer transition-colors duration-200 ${
                    isDarkMode
                      ? 'bg-gray-700 text-white border-gray-600'
                      : 'bg-white text-gray-900 border-gray-300'
                  } border focus:outline-none focus:ring-2 focus:ring-purple-500`}
                >
                  <option value="all">Todos</option>
                  {categories.map((cat) => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${
              isDarkMode ? 'border-purple-400' : 'border-purple-600'
            }`}></div>
            <p className="mt-4">Cargando documentos...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={`text-center py-12 ${
            isDarkMode ? 'text-red-400' : 'text-red-600'
          }`}>
            <p>{error}</p>
          </div>
        )}

        {/* Documents Grid */}
        {!loading && !error && (
          <>
            {filteredDocuments.length === 0 ? (
              <div className="text-center py-12">
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  {selectedCategory === 'all' 
                    ? 'No hay documentos disponibles' 
                    : `No hay documentos en la categoría "${selectedCategory}"`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => handleDocumentClick(doc.slug)}
                    className={`rounded-lg p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                      isDarkMode 
                        ? 'bg-gray-800 hover:bg-gray-700 shadow-lg hover:shadow-xl' 
                        : 'bg-white hover:bg-gray-50 shadow-md hover:shadow-lg'
                    }`}
                  >
                    <h3 className={`text-xl font-bold mb-2 ${
                      isDarkMode ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      {doc.title}
                    </h3>
                    
                    <p className={`text-sm mb-4 line-clamp-3 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {doc.summary}
                    </p>
                    
                    <button
                      className={`text-sm font-semibold transition-colors duration-300 ${
                        isDarkMode 
                          ? 'text-purple-400 hover:text-purple-300' 
                          : 'text-purple-600 hover:text-purple-700'
                      }`}
                    >
                      Leer más →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentosListPage;
